// Per-conversation line editor. Each row is editable inline; saving
// PATCHes only the changed fields against /api/admin/content/lines/<id>.

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    apiBase,
    generateAudio,
    generateDialogue,
    generateTranslation,
    getContentConversation,
    getSpeakers,
    getTtsPromptingTips,
    patchConversation,
    patchLine,
    populateLineActors,
    resliceDialogue,
} from '../adminApi';
import { Loading, Err } from './Overview';

// Actor + Sex are NOT here: a line's name/sex are derived from its
// assigned AI speaker (set via "Apply speakers to lines" or the per-line
// voice override), shown read-only in the Speaker column below.
const EDITABLE = [
    { key: 'text', label: 'Italian (text)', multi: true, w: 280 },
    { key: 'translation', label: 'Translation', multi: true, w: 240 },
    { key: 'literal_translation', label: 'Literal', multi: true, w: 220 },
    { key: 'position', label: 'Position', multi: false, w: 80 },
];

export default function ContentConversation({ match }) {
    const id = match?.params?.id;
    const [data, setData] = useState(null);
    const [speakers, setSpeakers] = useState([]);
    const [err, setErr] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const audioRef = useRef(null);
    // Seconds at which to auto-pause — many lines can share one dialogue
    // WAV, so each plays only its [t_start, t_stop] slice.
    const stopAtRef = useRef(null);
    const [playingLineId, setPlayingLineId] = useState(null);

    // Attach listeners once `data` is available — before that the
    // <audio> element isn't in the DOM (the page renders <Loading/>
    // and short-circuits), so audioRef.current is null on first mount.
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return undefined;
        const clear = () => setPlayingLineId(null);
        const onTime = () => {
            if (stopAtRef.current != null && audio.currentTime >= stopAtRef.current) {
                audio.pause(); // 'pause' listener clears state
            }
        };
        audio.addEventListener('ended', clear);
        audio.addEventListener('pause', clear);
        audio.addEventListener('error', clear);
        audio.addEventListener('timeupdate', onTime);
        return () => {
            audio.removeEventListener('ended', clear);
            audio.removeEventListener('pause', clear);
            audio.removeEventListener('error', clear);
            audio.removeEventListener('timeupdate', onTime);
        };
    }, [data]);

    const togglePlay = (line) => {
        const audio = audioRef.current;
        if (!audio || !line.audio_fname) return;
        if (playingLineId === line.id) {
            audio.pause();
            return; // 'pause' listener clears state
        }
        // Play only this line's slice. Dialogue-generated lines share one
        // WAV with per-line offsets; legacy per-line files use 0..duration
        // (whole file), so a missing/zero range falls back to the full file.
        const hasRange = Number.isFinite(line.t_start) && Number.isFinite(line.t_stop)
            && line.t_stop > line.t_start;
        const startSec = hasRange ? line.t_start / 1000 : 0;
        stopAtRef.current = hasRange ? line.t_stop / 1000 : null;
        // Cache-bust each play: regenerated audio reuses deterministic
        // filenames, so without this the browser would replay a stale file.
        audio.src = `${apiBase}/static/audio/${encodeURIComponent(line.audio_fname)}?v=${Date.now()}`;
        const seek = () => {
            try { audio.currentTime = startSec; } catch (e) { /* seek unsupported */ }
            audio.removeEventListener('loadedmetadata', seek);
        };
        audio.addEventListener('loadedmetadata', seek);
        audio.play().then(() => setPlayingLineId(line.id)).catch(() => setPlayingLineId(null));
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            try {
                const [d, s] = await Promise.all([
                    getContentConversation(id),
                    getSpeakers(),
                ]);
                if (!cancelled) {
                    setData(d);
                    setSpeakers(s?.items || []);
                }
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [id, reloadKey]);

    if (err) return <Err msg={err} />;
    if (!data) return <Loading />;

    const conv = data.conversation || data;
    const lines = data.lines || conv.lines || [];

    const isMiniCourse = conv.mini_course_id != null;
    const backTo = isMiniCourse
        ? `/admin/mini-courses/${conv.mini_course_id}`
        : '/admin/content';
    const backLabel = isMiniCourse ? '← Mini course' : '← Content';

    return (
        <div>
            <div style={styles.crumb}>
                <Link to={backTo} style={styles.crumbLink}>{backLabel}</Link>
            </div>
            <h2 style={styles.h2}>
                {conv.title || `Conversation #${conv.id}`}
                <StatusBadge conv={conv} />
            </h2>

            <ConversationHeaderForm conv={conv} onSaved={() => setReloadKey((k) => k + 1)} />

            <ConversationContentEditor conv={conv} onSaved={() => setReloadKey((k) => k + 1)} />

            <div style={styles.meta}>
                <Meta label="Speakers">
                    {[conv.left_actor, conv.right_actor].filter(Boolean).join(' · ') || '—'}
                </Meta>
                <Meta label="Lines">{lines.length}</Meta>
                <Meta label="With audio">
                    {lines.filter((l) => l.has_audio).length} / {lines.length}
                </Meta>
            </div>

            <SpeakerAssignmentBar
                conv={conv}
                lines={lines}
                speakers={speakers}
                onSaved={() => setReloadKey((k) => k + 1)}
            />

            <GenerationToolbar
                conv={conv}
                lines={lines}
                onLineUpdated={() => setReloadKey((k) => k + 1)}
            />

            <div style={styles.tableWrap}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <Th w={32}>#</Th>
                            {EDITABLE.map((f) => (
                                <Th key={f.key} w={f.w}>{f.label}</Th>
                            ))}
                            <Th w={120}>Speaker</Th>
                            <Th w={120}>Audio</Th>
                            <Th w={130} align="right">&nbsp;</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {lines.map((line, ix) => (
                            <LineRow
                                key={line.id}
                                index={ix + 1}
                                line={line}
                                conv={conv}
                                speakers={speakers}
                                isPlaying={playingLineId === line.id}
                                onTogglePlay={togglePlay}
                                onSaved={() => setReloadKey((k) => k + 1)}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Single hidden audio element shared across rows. Each row's
                play button swaps audio.src (with a cache-buster so a
                regenerated WAV plays fresh) and toggles playback. */}
            <audio ref={audioRef} preload="none" style={{ display: 'none' }} />
        </div>
    );
}

function isLive(conv) {
    return (
        conv.mini_course_id == null
        && conv.chapter != null
        && conv.lecture != null
        && conv.variant === 1
    );
}

function StatusBadge({ conv }) {
    if (conv.mini_course_id != null) {
        return <span style={{ ...styles.badge, ...styles.badgeMC }}>Mini course</span>;
    }
    if (isLive(conv)) {
        return <span style={{ ...styles.badge, ...styles.badgeLive }}>Live</span>;
    }
    return <span style={{ ...styles.badge, ...styles.badgeDraft }}>Draft</span>;
}

function ConversationHeaderForm({ conv, onSaved }) {
    const inMC = conv.mini_course_id != null;

    const [chapter, setChapter] = useState(conv.chapter ?? '');
    const [lecture, setLecture] = useState(conv.lecture ?? '');
    const [variant, setVariant] = useState(conv.variant ?? '');
    const [tags, setTags] = useState(conv.tags ?? '');
    const [slowSpeed, setSlowSpeed] = useState(conv.slow_speed ?? 0.8);
    const [isFreePreview, setIsFreePreview] = useState(!!conv.is_free_preview);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedTick, setSavedTick] = useState(false);

    // Toggling the "Free preview" checkbox commits immediately —
    // boolean flips don't need a separate Save step. Local state
    // flips first for snappy feedback, then we PATCH and refresh.
    const onToggleFreePreview = async (e) => {
        const next = e.target.checked;
        const prev = isFreePreview;
        setIsFreePreview(next);
        setError(null);
        try {
            await patchConversation(conv.id, { is_free_preview: next });
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (err) {
            setIsFreePreview(prev);
            setError(err?.response?.data?.error || err?.message || 'Save failed');
        }
    };

    const initial = {
        chapter: conv.chapter ?? '',
        lecture: conv.lecture ?? '',
        variant: conv.variant ?? '',
        tags: conv.tags ?? '',
        slow_speed: conv.slow_speed ?? 0.8,
    };
    const current = { chapter, lecture, variant, tags, slow_speed: slowSpeed };
    const fields = inMC
        ? ['lecture', 'tags', 'slow_speed']
        : ['chapter', 'lecture', 'variant', 'tags', 'slow_speed'];
    const dirty = fields.some((k) => String(current[k]) !== String(initial[k]));

    const onSave = async () => {
        if (!dirty || saving) return;
        const payload = {};
        fields.forEach((k) => {
            const a = String(current[k]);
            const b = String(initial[k]);
            if (a !== b) {
                payload[k] = current[k] === '' ? null : current[k];
            }
        });
        try {
            setSaving(true);
            setError(null);
            await patchConversation(conv.id, payload);
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'Save failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const onReset = () => {
        setChapter(initial.chapter);
        setLecture(initial.lecture);
        setVariant(initial.variant);
        setTags(initial.tags);
        setSlowSpeed(initial.slow_speed);
        setError(null);
    };

    return (
        <div style={styles.headerForm}>
            {inMC ? (
                <div style={styles.headerLabel}>Mini course #{conv.mini_course_id}</div>
            ) : null}
            <div style={styles.headerFields}>
                {!inMC ? (
                    <NumField label="Chapter" value={chapter} onChange={setChapter} disabled={saving} />
                ) : null}
                <NumField label="Lecture" value={lecture} onChange={setLecture} disabled={saving} />
                {!inMC ? (
                    <NumField label="Variant" value={variant} onChange={setVariant} disabled={saving} />
                ) : null}
                <TextField
                    label="Tags"
                    value={tags}
                    onChange={setTags}
                    disabled={saving}
                    placeholder="comma-separated"
                />
                <NumField
                    label="Slow speed (.8× toggle)"
                    value={slowSpeed}
                    onChange={setSlowSpeed}
                    disabled={saving}
                    step={0.05}
                    min={0.5}
                    max={1}
                />
                {dirty ? (
                    <>
                        <button type="button" style={styles.btnLink} onClick={onReset} disabled={saving}>
                            Reset
                        </button>
                        <button type="button" style={styles.btnPrimary} onClick={onSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </>
                ) : null}
                {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
            </div>
            <label style={styles.freePreviewRow}>
                <input
                    type="checkbox"
                    checked={isFreePreview}
                    onChange={onToggleFreePreview}
                />
                <span>Free preview — accessible without a subscription</span>
            </label>
            {error ? <div style={styles.err}>{error}</div> : null}
            {!inMC ? (
                <div style={styles.headerHint}>
                    A main-course conversation goes Live when chapter, lecture, and variant 1 are all set.
                </div>
            ) : null}
        </div>
    );
}

// Edits the per-conversation lesson/about HTML + lesson type. Content is raw
// HTML (grammar tables + <PlayWordButton> audio tags) — an internal admin tool,
// so plain textareas + a markup cheat-sheet rather than a rich editor.
function ConversationContentEditor({ conv, onSaved }) {
    const [about, setAbout] = useState(conv.about ?? '');
    const [lesson, setLesson] = useState(conv.lesson ?? '');
    const [lessonType, setLessonType] = useState(conv.lesson_type ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedTick, setSavedTick] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    const dirty =
        (about ?? '') !== (conv.about ?? '') ||
        (lesson ?? '') !== (conv.lesson ?? '') ||
        (lessonType ?? '') !== (conv.lesson_type ?? '');

    const onSave = async () => {
        if (!dirty || saving) return;
        try {
            setSaving(true);
            setError(null);
            await patchConversation(conv.id, {
                about,
                lesson,
                lesson_type: lessonType || null,
            });
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.contentEditor}>
            <div style={styles.contentHeaderRow}>
                <h3 style={styles.contentH3}>Lesson &amp; About</h3>
                <button type="button" style={styles.btnLink} onClick={() => setShowHelp((v) => !v)}>
                    {showHelp ? 'Hide markup help' : 'Markup help'}
                </button>
            </div>

            {showHelp ? (
                <div style={styles.cheatSheet}>
                    <p style={{ margin: '0 0 6px' }}>Supported HTML (renders in the mobile app):</p>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        <li>Text: <code>&lt;p&gt;</code>, <code>&lt;h5&gt;</code>, <code>&lt;li&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;b&gt;</code>, <code>&lt;br&gt;</code></li>
                        <li>Tables: <code>&lt;table&gt;&lt;tr&gt;&lt;th&gt;…&lt;/th&gt;&lt;td&gt;…&lt;/td&gt;&lt;/tr&gt;&lt;/table&gt;</code></li>
                        <li>Play a word: <code>&lt;PlayWordButton word="faccia" translation="face" /&gt;</code> or <code>&lt;PlayWord id="12" /&gt;</code></li>
                    </ul>
                    <p style={{ margin: '6px 0 0', color: '#877963' }}>
                        Other tags (divs/layout, SVG) are ignored by the app — keep to the above.
                    </p>
                </div>
            ) : null}

            <label style={styles.contentLabel}>Lesson type</label>
            <select
                style={styles.contentSelect}
                value={lessonType}
                onChange={(e) => setLessonType(e.target.value)}
                disabled={saving}
            >
                <option value="">— none —</option>
                <option value="Grammar">Grammar</option>
                <option value="Culture">Culture</option>
            </select>

            <label style={styles.contentLabel}>About (HTML)</label>
            <textarea
                style={styles.contentTextarea}
                value={about}
                onChange={(e) => setAbout(e.target.value)}
                disabled={saving}
                spellCheck={false}
            />

            <label style={styles.contentLabel}>Lesson (HTML)</label>
            <textarea
                style={{ ...styles.contentTextarea, minHeight: 260 }}
                value={lesson}
                onChange={(e) => setLesson(e.target.value)}
                disabled={saving}
                spellCheck={false}
            />

            <div style={styles.contentActions}>
                {dirty ? (
                    <button type="button" style={styles.btnPrimary} onClick={onSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save content'}
                    </button>
                ) : null}
                {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
                {error ? <span style={styles.err}>{error}</span> : null}
            </div>
        </div>
    );
}

function NumField({ label, value, onChange, disabled, step, min = 0, max }) {
    return (
        <label style={styles.numField}>
            <span style={styles.numLabel}>{label}</span>
            <input
                type="number"
                style={styles.numInput}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                step={step}
                min={min}
                max={max}
            />
        </label>
    );
}

function TextField({ label, value, onChange, disabled, placeholder }) {
    return (
        <label style={styles.numField}>
            <span style={styles.numLabel}>{label}</span>
            <input
                type="text"
                style={styles.tagInput}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                placeholder={placeholder}
            />
        </label>
    );
}

function SpeakerAssignmentBar({ conv, lines, speakers, onSaved }) {
    const [left, setLeft] = useState(conv.left_speaker_id ?? '');
    const [right, setRight] = useState(conv.right_speaker_id ?? '');
    const [saving, setSaving] = useState(null); // 'left' | 'right' | null
    const [populating, setPopulating] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    const save = async (which, value) => {
        const field = which === 'left' ? 'left_speaker_id' : 'right_speaker_id';
        try {
            setSaving(which);
            setError(null);
            await patchConversation(conv.id, { [field]: value === '' ? null : Number(value) });
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Save failed');
        } finally {
            setSaving(null);
        }
    };

    const onLeftChange = (v) => {
        setLeft(v);
        save('left', v);
    };
    const onRightChange = (v) => {
        setRight(v);
        save('right', v);
    };

    const findSpeaker = (id) => speakers.find((s) => s.id === Number(id));
    const leftSpeaker = left ? findSpeaker(left) : null;
    const rightSpeaker = right ? findSpeaker(right) : null;
    const targets = lines.filter((l) => {
        const pos = (l.position || '').toLowerCase();
        const sp = pos === 'l' ? leftSpeaker : pos === 'r' ? rightSpeaker : null;
        if (!sp) return false;
        return l.speaker_id !== sp.id || l.actor !== sp.name || l.sex !== sp.sex;
    });

    const onPopulate = async () => {
        if (populating || targets.length === 0) return;
        setError(null);
        setInfo(null);
        try {
            setPopulating(true);
            const res = await populateLineActors(conv.id);
            setInfo(`Populated ${res.updated} line${res.updated === 1 ? '' : 's'}.`);
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Populate failed');
        } finally {
            setPopulating(false);
        }
    };

    return (
        <div style={styles.speakerBar}>
            <div style={styles.genTitle}>Conversation speakers</div>
            <SpeakerPicker
                label="Left (l)"
                value={left}
                speakers={speakers}
                disabled={saving === 'left' || populating}
                onChange={onLeftChange}
            />
            <SpeakerPicker
                label="Right (r)"
                value={right}
                speakers={speakers}
                disabled={saving === 'right' || populating}
                onChange={onRightChange}
            />
            <button
                type="button"
                style={styles.genBtn}
                title="Assign each conversation speaker to its matching lines (sets the AI speaker + derived name/sex)"
                onClick={onPopulate}
                disabled={populating || targets.length === 0}
            >
                {populating
                    ? 'Applying…'
                    : `Apply speakers to lines (${targets.length})`}
            </button>
            <Link to="/admin/speakers" style={styles.speakerLink}>Manage speakers →</Link>
            {info ? <div style={{ ...styles.saved, marginLeft: 10 }}>{info}</div> : null}
            {error ? <div style={{ ...styles.err, marginLeft: 10 }}>{error}</div> : null}
        </div>
    );
}

function SpeakerPicker({ label, value, speakers, disabled, onChange }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={styles.numLabel}>{label}</span>
            <select
                style={{ ...styles.numInput, width: 200 }}
                value={value ?? ''}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
            >
                <option value="">— none —</option>
                {speakers.map((s) => (
                    <option key={s.id} value={s.id}>
                        {s.name} · {s.voice}{s.sex ? ` (${s.sex})` : ''}
                    </option>
                ))}
            </select>
        </label>
    );
}

function LineRow({ index, line, conv, speakers, isPlaying, onTogglePlay, onSaved }) {
    const [draft, setDraft] = useState(() => pickEditable(line));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedTick, setSavedTick] = useState(false);

    const dirty = EDITABLE.some((f) => (draft[f.key] ?? '') !== (line[f.key] ?? ''));

    const onChange = (key, value) => {
        setDraft((d) => ({ ...d, [key]: value }));
        setError(null);
    };

    const onSave = async () => {
        if (!dirty || saving) return;
        const changed = {};
        EDITABLE.forEach((f) => {
            const a = draft[f.key] ?? '';
            const b = line[f.key] ?? '';
            if (a !== b) changed[f.key] = draft[f.key];
        });
        try {
            setSaving(true);
            await patchLine(line.id, changed);
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'Save failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const onReset = () => {
        setDraft(pickEditable(line));
        setError(null);
    };

    return (
        <tr style={dirty ? styles.rowDirty : undefined}>
            <Td><span style={styles.indexCell}>{index}</span></Td>
            {EDITABLE.map((f) => (
                <Td key={f.key}>
                    {f.multi ? (
                        <textarea
                            rows={2}
                            style={styles.textarea}
                            value={draft[f.key] || ''}
                            onChange={(e) => onChange(f.key, e.target.value)}
                            disabled={saving}
                        />
                    ) : (
                        <input
                            style={styles.input}
                            value={draft[f.key] || ''}
                            onChange={(e) => onChange(f.key, e.target.value)}
                            disabled={saving}
                        />
                    )}
                </Td>
            ))}
            <Td>
                <span style={styles.speakerCell}>
                    {line.actor
                        ? `${line.actor}${line.sex ? ` (${line.sex})` : ''}`
                        : <span style={styles.speakerCellEmpty}>— assign —</span>}
                </span>
            </Td>
            <Td><AudioCell line={line} isPlaying={isPlaying} onTogglePlay={onTogglePlay} onSaved={onSaved} /></Td>
            <Td align="right">
                {error ? <div style={styles.err}>{error}</div> : null}
                {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
                {dirty ? (
                    <div style={{ marginBottom: 4 }}>
                        <button
                            type="button"
                            style={styles.btnLink}
                            onClick={onReset}
                            disabled={saving}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            style={styles.btnPrimary}
                            onClick={onSave}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                ) : null}
                <GenerateRowButtons
                    line={line}
                    conv={conv}
                    speakers={speakers}
                    onUpdated={onSaved}
                />
            </Td>
        </tr>
    );
}

function GenerateRowButtons({ line, conv, speakers, onUpdated }) {
    const [voiceOpen, setVoiceOpen] = useState(false);
    return (
        <>
            <GenerateButtonsInner line={line} onUpdated={onUpdated} onVoiceOpen={() => setVoiceOpen(true)} />
            {voiceOpen ? (
                <VoiceOverrideModal
                    line={line}
                    conv={conv}
                    speakers={speakers}
                    onClose={() => setVoiceOpen(false)}
                    onSaved={() => { setVoiceOpen(false); onUpdated && onUpdated(); }}
                />
            ) : null}
        </>
    );
}

function GenerateButtonsInner({ line, onUpdated, onVoiceOpen }) {
    const [busy, setBusy] = useState(null); // 't' | 'a' | null
    const [err, setErr] = useState(null);

    const run = async (kind) => {
        if (busy) return;
        setBusy(kind);
        setErr(null);
        try {
            if (kind === 't') {
                await generateTranslation(line.id, { overwrite: true });
            } else {
                await generateAudio(line.id, { overwrite: true });
            }
            onUpdated && onUpdated();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'Generation failed';
            setErr(msg);
        } finally {
            setBusy(null);
        }
    };

    const hasText = (line.text || '').trim().length > 0;
    const hasOverride = line.speaker_id != null || (line.instructions || '').trim() !== '';

    return (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
            <button
                type="button"
                title={hasOverride ? 'Voice override active — click to edit' : 'Voice override (speaker / instructions for this line)'}
                style={hasOverride ? styles.btnGenVoiceActive : styles.btnGenVoice}
                onClick={onVoiceOpen}
                disabled={busy !== null}
            >
                {hasOverride ? 'V●' : 'V'}
            </button>
            <button
                type="button"
                title="Generate translation + literal via OpenAI"
                style={styles.btnGenT}
                onClick={() => run('t')}
                disabled={!hasText || busy !== null}
            >
                {busy === 't' ? '…' : 'T↻'}
            </button>
            <button
                type="button"
                title="Generate TTS audio via OpenAI"
                style={styles.btnGenA}
                onClick={() => run('a')}
                disabled={!hasText || busy !== null}
            >
                {busy === 'a' ? '…' : 'A↻'}
            </button>
            {err ? <div style={{ ...styles.err, marginLeft: 6 }}>{err}</div> : null}
        </div>
    );
}

function VoiceOverrideModal({ line, conv, speakers, onClose, onSaved }) {
    const [speakerId, setSpeakerId] = useState(line.speaker_id ?? '');
    const [instructions, setInstructions] = useState(line.instructions || '');
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [showTips, setShowTips] = useState(false);
    const [tips, setTips] = useState(null);
    const [tipsErr, setTipsErr] = useState(null);

    // Lazy-load the Eleven v3 tips the first time the panel is opened.
    const toggleTips = async () => {
        const next = !showTips;
        setShowTips(next);
        if (next && !tips && !tipsErr) {
            try {
                setTips(await getTtsPromptingTips());
            } catch (e) {
                setTipsErr(e?.response?.data?.error || e?.message || 'Could not load tips');
            }
        }
    };

    const findSpeaker = (id) => speakers.find((s) => s.id === Number(id));

    // What the conversation default would resolve to for this line's position.
    const convDefaultId = line.position === 'l' ? conv.left_speaker_id
        : line.position === 'r' ? conv.right_speaker_id
        : null;
    const convDefault = convDefaultId ? findSpeaker(convDefaultId) : null;
    const overrideSpeaker = speakerId ? findSpeaker(speakerId) : null;
    const effectiveSpeaker = overrideSpeaker || convDefault;
    const effectiveInstructions = (instructions || '').trim()
        || (effectiveSpeaker?.baseline_instructions || '').trim()
        || '';

    const save = async (alsoGenerate) => {
        try {
            setSaving(true);
            setError(null);
            await patchLine(line.id, {
                speaker_id: speakerId === '' ? null : Number(speakerId),
                instructions: (instructions || '').trim() || null,
            });
            if (alsoGenerate) {
                setGenerating(true);
                await generateAudio(line.id, { overwrite: true });
            }
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Save failed');
        } finally {
            setSaving(false);
            setGenerating(false);
        }
    };

    return (
        <div style={styles.modalBackdrop} onClick={onClose}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <h3 style={styles.modalTitle}>
                    Voice override · line {line.line ?? line.id}
                </h3>
                <p style={styles.modalSub}>{line.text}</p>

                <div style={styles.modalRow}>
                    <span style={styles.modalLabel}>Speaker</span>
                    <select
                        style={styles.modalSelect}
                        value={speakerId}
                        onChange={(e) => setSpeakerId(e.target.value)}
                        disabled={saving}
                    >
                        <option value="">
                            — use conversation default{convDefault ? ` (${convDefault.name})` : ''} —
                        </option>
                        {speakers.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name} · {s.voice}{s.sex ? ` (${s.sex})` : ''}
                            </option>
                        ))}
                    </select>
                </div>

                <div style={styles.modalRow}>
                    <span style={styles.modalLabel}>Instructions</span>
                    <textarea
                        rows={3}
                        style={styles.modalTextarea}
                        placeholder={
                            (effectiveSpeaker?.baseline_instructions)
                                ? `Default: ${effectiveSpeaker.baseline_instructions}`
                                : 'Voice steering for this line (leave blank to use speaker baseline)'
                        }
                        value={instructions}
                        onChange={(e) => setInstructions(e.target.value)}
                        disabled={saving}
                    />
                </div>

                <div style={{ margin: '0 0 12px' }}>
                    <button type="button" style={styles.btnLink} onClick={toggleTips}>
                        {showTips ? 'Hide Eleven v3 tips' : 'Eleven v3 prompting tips'}
                    </button>
                    {showTips ? (
                        <div style={styles.cheatSheet}>
                            {tipsErr ? <div style={styles.err}>{tipsErr}</div>
                                : !tips ? <div>Loading…</div>
                                : <TtsTips tips={tips} />}
                        </div>
                    ) : null}
                </div>

                <div style={styles.modalEffective}>
                    <div><b>Will use:</b> {effectiveSpeaker ? `${effectiveSpeaker.name} · ${effectiveSpeaker.voice}` : '(legacy fallback — sex/position → default voice)'}</div>
                    {effectiveInstructions ? <div style={{ marginTop: 4 }}><b>Steering:</b> {effectiveInstructions}</div> : null}
                </div>

                {error ? <div style={styles.err}>{error}</div> : null}

                <div style={styles.modalActions}>
                    <button type="button" style={styles.btnLink} onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        style={styles.btnLink}
                        onClick={() => { setSpeakerId(''); setInstructions(''); }}
                        disabled={saving}
                    >
                        Clear override
                    </button>
                    <button
                        type="button"
                        style={styles.btnPrimary}
                        onClick={() => save(false)}
                        disabled={saving}
                    >
                        {saving && !generating ? 'Saving…' : 'Save'}
                    </button>
                    <button
                        type="button"
                        style={styles.btnGenA}
                        onClick={() => save(true)}
                        disabled={saving}
                    >
                        {generating ? 'Generating…' : 'Save + regenerate audio'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Renders the Eleven v3 prompting tips returned by the backend
// (sections -> items{label, detail, example?} + notes), generically so
// the backend stays the single source of truth.
function TtsTips({ tips }) {
    return (
        <div>
            {tips.summary ? <p style={{ margin: '0 0 8px' }}>{tips.summary}</p> : null}
            {(tips.sections || []).map((sec, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>{sec.title}</div>
                    {sec.intro ? <div style={{ color: '#877963', marginBottom: 4 }}>{sec.intro}</div> : null}
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {(sec.items || []).map((it, j) => (
                            <li key={j} style={{ marginBottom: 2 }}>
                                <b>{it.label}:</b> {it.detail}
                                {it.example ? <> <code>{it.example}</code></> : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ))}
            {(tips.notes || []).length ? (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#877963' }}>
                    {tips.notes.map((n, i) => <li key={i} style={{ marginBottom: 2 }}>{n}</li>)}
                </ul>
            ) : null}
        </div>
    );
}

function GenerationToolbar({ conv, lines, onLineUpdated }) {
    const [progress, setProgress] = useState(null); // {done, total} | null (translations)
    const [dialogueRunning, setDialogueRunning] = useState(false);
    const [resliceRunning, setResliceRunning] = useState(false);
    const [error, setError] = useState(null);

    const linesWithText = lines.filter((l) => (l.text || '').trim());
    const missingTranslation = linesWithText.filter(
        (l) => !(l.translation || '').trim() || !(l.literal_translation || '').trim(),
    );

    const inflight = progress !== null || dialogueRunning || resliceRunning;

    // Translations still generate per line (one OpenAI call each).
    const onGenMissingT = async () => {
        if (inflight || missingTranslation.length === 0) return;
        if (!window.confirm(
            `Generate translations for ${missingTranslation.length} line${missingTranslation.length === 1 ? '' : 's'} via OpenAI?`,
        )) return;
        setError(null);
        setProgress({ done: 0, total: missingTranslation.length });
        for (let i = 0; i < missingTranslation.length; i++) {
            const line = missingTranslation[i];
            try {
                await generateTranslation(line.id, { overwrite: false });
            } catch (e) {
                const msg = e?.response?.data?.error || e?.message || 'Generation failed';
                setError(`Line ${line.line ?? line.id}: ${msg}`);
                setProgress(null);
                onLineUpdated && onLineUpdated();
                return;
            }
            setProgress({ done: i + 1, total: missingTranslation.length });
        }
        setProgress(null);
        onLineUpdated && onLineUpdated();
    };

    // Audio is generated for the whole conversation in ONE ElevenLabs Text
    // to Dialogue request; the backend slices it into per-line offsets.
    const onGenDialogue = async () => {
        if (inflight || linesWithText.length === 0) return;
        if (!window.confirm(
            `Generate full dialogue audio for the whole conversation (${linesWithText.length} line${linesWithText.length === 1 ? '' : 's'}) with ElevenLabs Text to Dialogue? This OVERWRITES existing audio.`,
        )) return;
        setError(null);
        setDialogueRunning(true);
        try {
            await generateDialogue(conv.id);
            onLineUpdated && onLineUpdated();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Dialogue generation failed');
        } finally {
            setDialogueRunning(false);
        }
    };

    // Recompute line timings from the existing dialogue WAV(s) — no
    // ElevenLabs call. Handy after tuning the silence thresholds.
    const onReslice = async () => {
        if (inflight) return;
        setError(null);
        setResliceRunning(true);
        try {
            const res = await resliceDialogue(conv.id);
            if (res?.failed?.length) {
                setError(`Re-sliced, but couldn't split: ${res.failed.join(', ')}.`);
            }
            onLineUpdated && onLineUpdated();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Re-slice failed');
        } finally {
            setResliceRunning(false);
        }
    };

    return (
        <div style={styles.genBar}>
            <div style={styles.genTitle}>Auto-generate</div>
            <button
                type="button"
                style={styles.genBtn}
                onClick={onGenMissingT}
                disabled={inflight || missingTranslation.length === 0}
            >
                {progress
                    ? `Translating ${progress.done}/${progress.total}…`
                    : `Generate missing translations (${missingTranslation.length})`}
            </button>
            <button
                type="button"
                style={styles.genBtnDanger}
                onClick={onGenDialogue}
                disabled={inflight || linesWithText.length === 0}
                title="Generate the whole conversation in one ElevenLabs Text to Dialogue request (overwrites existing audio)"
            >
                {dialogueRunning
                    ? 'Generating dialogue…'
                    : `Generate full dialogue audio (${linesWithText.length})`}
            </button>
            <button
                type="button"
                style={styles.genBtn}
                onClick={onReslice}
                disabled={inflight}
                title="Recompute line timings from the existing dialogue audio (no ElevenLabs call)"
            >
                {resliceRunning ? 'Re-slicing…' : 'Re-slice from audio'}
            </button>
            {error ? <div style={{ ...styles.err, marginLeft: 10 }}>{error}</div> : null}
        </div>
    );
}

function AudioCell({ line, isPlaying, onTogglePlay, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [start, setStart] = useState(line.t_start ?? '');
    const [stop, setStop] = useState(line.t_stop ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Re-sync the inputs if the row reloads with new offsets (e.g. after a
    // re-slice / regenerate).
    useEffect(() => {
        setStart(line.t_start ?? '');
        setStop(line.t_stop ?? '');
    }, [line.t_start, line.t_stop]);

    const cancel = () => {
        setEditing(false);
        setStart(line.t_start ?? '');
        setStop(line.t_stop ?? '');
        setError(null);
    };

    const save = async () => {
        const s = Number(start);
        const e2 = Number(stop);
        if (!Number.isInteger(s) || !Number.isInteger(e2) || s < 0 || e2 <= s) {
            setError('Whole ms, stop > start.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await patchLine(line.id, { t_start: s, t_stop: e2 });
            setEditing(false);
            onSaved && onSaved();
        } catch (err) {
            setError(err?.response?.data?.error || err?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (!line.has_audio) {
        return <span style={{ color: '#c0392b', fontWeight: 600, fontSize: 12 }}>missing</span>;
    }
    const canPlay = !!line.audio_fname && !!onTogglePlay;
    const hasRange = Number.isFinite(line.t_start) && Number.isFinite(line.t_stop);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {canPlay ? (
                    <button
                        type="button"
                        title={isPlaying ? 'Stop' : 'Play this line'}
                        style={isPlaying ? styles.btnPlayActive : styles.btnPlay}
                        onClick={() => onTogglePlay(line)}
                    >
                        {isPlaying ? '■' : '▶'}
                    </button>
                ) : null}
                <span style={{ color: '#2e7d32', fontWeight: 600, fontSize: 12 }}>present</span>
            </div>
            {line.audio_fname ? (
                <span title={line.audio_fname} style={styles.fname}>
                    {line.audio_fname}
                </span>
            ) : (
                <span style={{ color: '#888', fontSize: 11 }}>(blob)</span>
            )}
            {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input
                            type="number"
                            style={styles.msInput}
                            value={start}
                            onChange={(e) => setStart(e.target.value)}
                            disabled={saving}
                            title="start (ms)"
                        />
                        <span style={{ color: '#888' }}>–</span>
                        <input
                            type="number"
                            style={styles.msInput}
                            value={stop}
                            onChange={(e) => setStop(e.target.value)}
                            disabled={saving}
                            title="stop (ms)"
                        />
                        <span style={{ color: '#888', fontSize: 11 }}>ms</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" style={styles.btnLink} onClick={cancel} disabled={saving}>Cancel</button>
                        <button type="button" style={styles.btnPrimary} onClick={save} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                    {error ? <div style={styles.err}>{error}</div> : null}
                </div>
            ) : (
                <span style={styles.fname}>
                    {hasRange ? `${(line.t_start / 1000).toFixed(1)}–${(line.t_stop / 1000).toFixed(1)}s` : 'no range'}
                    <button
                        type="button"
                        title="Edit time range (ms)"
                        style={styles.pencilBtn}
                        onClick={() => setEditing(true)}
                    >
                        ✎
                    </button>
                </span>
            )}
        </div>
    );
}

function pickEditable(line) {
    const out = {};
    EDITABLE.forEach((f) => { out[f.key] = line[f.key] ?? ''; });
    return out;
}

function Meta({ label, children }) {
    return (
        <div style={styles.metaCell}>
            <div style={styles.metaLabel}>{label}</div>
            <div style={styles.metaValue}>{children}</div>
        </div>
    );
}

function Th({ children, w, align }) {
    return <th style={{ ...styles.th, width: w, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align }) {
    return <td style={{ ...styles.td, textAlign: align || 'left' }}>{children}</td>;
}

const styles = {
    crumb: { marginBottom: 8 },
    crumbLink: { color: '#888', textDecoration: 'none', fontSize: 13 },
    h2: { marginTop: 0, color: '#222' },
    h2sub: { color: '#888', fontWeight: 400, fontSize: 14 },
    meta: {
        display: 'flex',
        gap: 24,
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    },
    metaCell: { display: 'flex', flexDirection: 'column', gap: 2 },
    metaLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
    metaValue: { color: '#222', fontSize: 14 },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, verticalAlign: 'bottom' },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222', verticalAlign: 'top' },
    rowDirty: { backgroundColor: '#fff8ef' },
    indexCell: { color: '#888', fontVariantNumeric: 'tabular-nums' },
    msInput: { width: 64, padding: '2px 4px', borderRadius: 4, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 12 },
    pencilBtn: { marginLeft: 6, padding: 0, border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 12 },
    speakerCell: { fontSize: 13, color: '#352F27' },
    speakerCellEmpty: { fontSize: 12, color: '#bbb', fontStyle: 'italic' },
    textarea: { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13, resize: 'vertical' },
    input: { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13 },
    fname: { color: '#888', fontSize: 11, maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    btnPrimary: { padding: '6px 12px', borderRadius: 6, border: '1px solid #d97447', background: '#d97447', color: '#fff', fontWeight: 600, cursor: 'pointer', marginLeft: 6 },
    btnLink: { padding: '6px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', fontSize: 12 },
    err: { color: '#c0392b', fontSize: 12, marginBottom: 4 },
    contentEditor: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    contentHeaderRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    contentH3: { margin: 0, fontSize: 15, color: '#222' },
    cheatSheet: { background: '#FBF7F0', border: '1px solid #EFE7DA', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#352F27', margin: '8px 0' },
    contentLabel: { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 4px' },
    contentSelect: { padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13 },
    contentTextarea: { width: '100%', minHeight: 120, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.5, resize: 'vertical' },
    contentActions: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 },
    saved: { color: '#2e7d32', fontSize: 12, fontWeight: 600, marginRight: 6 },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', marginLeft: 10, verticalAlign: 'middle' },
    badgeLive: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    badgeDraft: { backgroundColor: '#fff3e0', color: '#d97447' },
    badgeMC: { backgroundColor: '#ede7f6', color: '#5e35b1' },
    headerForm: {
        padding: '12px 16px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    },
    headerLabel: { fontSize: 11, color: '#5e35b1', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontWeight: 700 },
    headerFields: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' },
    freePreviewRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, fontSize: 13, color: '#444', cursor: 'pointer' },
    headerHint: { fontSize: 12, color: '#888', marginTop: 8 },
    numField: { display: 'flex', flexDirection: 'column', gap: 4 },
    numLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 },
    numInput: { width: 80, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13 },
    tagInput: { width: 220, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'inherit', fontSize: 13 },
    genBar: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 14px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        flexWrap: 'wrap',
    },
    genTitle: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, marginRight: 4 },
    genBtn: {
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid #5e35b1',
        background: '#fff',
        color: '#5e35b1',
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: 12,
    },
    genBtnDanger: {
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid #c0392b',
        background: '#fff',
        color: '#c0392b',
        fontWeight: 600,
        cursor: 'pointer',
        fontSize: 12,
    },
    btnGenT: {
        padding: '4px 8px',
        borderRadius: 5,
        border: '1px solid #5e35b1',
        background: '#fff',
        color: '#5e35b1',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 11,
        minWidth: 32,
    },
    btnGenA: {
        padding: '4px 8px',
        borderRadius: 5,
        border: '1px solid #2e7d32',
        background: '#fff',
        color: '#2e7d32',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 11,
        minWidth: 32,
    },
    btnGenVoice: {
        padding: '4px 8px',
        borderRadius: 5,
        border: '1px solid #888',
        background: '#fff',
        color: '#555',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 11,
        minWidth: 32,
    },
    btnGenVoiceActive: {
        padding: '4px 8px',
        borderRadius: 5,
        border: '1px solid #5e35b1',
        background: '#5e35b1',
        color: '#fff',
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 11,
        minWidth: 32,
    },
    btnPlay: {
        width: 26,
        height: 26,
        borderRadius: 13,
        border: '1px solid #2e7d32',
        background: '#fff',
        color: '#2e7d32',
        fontSize: 11,
        cursor: 'pointer',
        padding: 0,
        lineHeight: '24px',
    },
    btnPlayActive: {
        width: 26,
        height: 26,
        borderRadius: 13,
        border: '1px solid #2e7d32',
        background: '#2e7d32',
        color: '#fff',
        fontSize: 11,
        cursor: 'pointer',
        padding: 0,
        lineHeight: '24px',
    },
    speakerBar: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 12,
        padding: '10px 14px',
        backgroundColor: '#fff',
        borderRadius: 12,
        marginBottom: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        flexWrap: 'wrap',
    },
    speakerLink: { color: '#d97447', textDecoration: 'none', fontSize: 12, fontWeight: 600, marginBottom: 4 },
    modalBackdrop: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#fff',
        borderRadius: 12,
        padding: 20,
        width: 520,
        maxWidth: '90vw',
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },
    modalTitle: { margin: '0 0 4px', color: '#222', fontSize: 16 },
    modalSub: { margin: '0 0 14px', color: '#666', fontSize: 13, fontStyle: 'italic' },
    modalRow: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
    modalLabel: { fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 },
    modalSelect: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, fontFamily: 'inherit' },
    modalTextarea: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' },
    modalEffective: {
        backgroundColor: '#f5f0ff',
        border: '1px solid #ede5ff',
        borderRadius: 8,
        padding: 10,
        fontSize: 12,
        color: '#444',
        marginBottom: 12,
    },
    modalActions: {
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 8,
        flexWrap: 'wrap',
    },
};
